import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  UploadFile as UploadFileIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowRight as ExpandLessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';
import EmployeeDocuments from '../../components/client-employees/EmployeeDocuments';
import AddClientEmployeeDialog from '../../components/client-employees/AddClientEmployeeDialog';
import UploadClientEmployeeDocumentsDialog from '../../components/client-employees/UploadClientEmployeeDocumentsDialog';
import { portalEmployeeApi } from '../../components/client-employees/employeeApi';
import { portalFeaturesForSession } from '../../services/portal/portalFeatures.service';

// Same toolbar as the other portal lists: one 32px row.
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input': { fontSize: '0.8125rem' },
};

const employeeName = (emp) => emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(' ') || '—';

/**
 * Portal › Work › Employees: the client's own employees and their documents
 * (thaklis-doc 14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §4.4). Same cards and dialogs as the
 * typing center's client › Employees tab; no Delete here (typing center only). Viewers are read-only.
 */
const PortalEmployees = ({ portalUser }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const api = portalEmployeeApi;
  const canWrite = portalUser?.role !== 'ORG_VIEWER';
  const features = portalFeaturesForSession(portalUser);

  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [expandedUid, setExpandedUid] = useState(null);
  const [docsByUid, setDocsByUid] = useState({});
  const [viewingDoc, setViewingDoc] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  // Success notes close on their own; warnings and errors stay until closed.
  useEffect(() => {
    if (notice?.severity !== 'success') return undefined;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const loadEmployees = useCallback(async (p, size) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.list(p, size);
      const payload = resp?.data?.data || resp?.data || {};
      // Paginated envelope { content, totalElements }; tolerate a bare array too.
      const list = Array.isArray(payload) ? payload : (payload.content || []);
      setEmployees(list);
      setTotal(Array.isArray(payload) ? list.length : (payload.totalElements ?? list.length));
      setExpandedUid(null);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadEmployees(page, rowsPerPage); }, [loadEmployees, page, rowsPerPage]);

  // Expiry tiles come from the document-expiry report, when this client has it.
  useEffect(() => {
    if (!features.DOC_EXPIRY) return;
    ApiService.getPortalDocumentExpirySummary()
      .then((res) => setExpiry(res.data?.data || null))
      .catch(() => setExpiry(null));
  }, [features.DOC_EXPIRY]);

  // Document types for uploads: the typing center's own list (server falls back to the Thaklis list).
  useEffect(() => {
    if (!canWrite) return;
    ApiService.getPortalTcDocumentTypes()
      .then((res) => setDocumentTypes((res.data?.data || [])
        .filter((dt) => (dt.holder_type || dt.holderType) !== 'ORGANIZATION')
        .map((dt) => ({ code: dt.code, label: dt.label, category: dt.category }))))
      .catch(() => setDocumentTypes([]));
  }, [canWrite]);

  const loadDocs = useCallback(async (uid) => {
    setDocsByUid((prev) => ({ ...prev, [uid]: { loading: true, error: null, groups: [] } }));
    try {
      const resp = await api.documents(uid);
      const payload = resp?.data?.data || resp?.data || {};
      setDocsByUid((prev) => ({ ...prev, [uid]: { loading: false, error: null, groups: Array.isArray(payload?.groups) ? payload.groups : [] } }));
    } catch (e) {
      setDocsByUid((prev) => ({ ...prev, [uid]: { loading: false, error: e?.response?.data?.error || 'Failed to load documents', groups: [] } }));
    }
  }, [api]);

  const toggle = (uid) => {
    if (!uid) return;
    if (expandedUid === uid) { setExpandedUid(null); return; }
    setExpandedUid(uid);
    if (!docsByUid[uid]) loadDocs(uid);
  };

  const handleView = async (filePath, key) => {
    if (!api.view) return;
    setViewingDoc(key);
    try {
      const resp = await api.view(filePath);
      const blob = new Blob([resp.data], { type: resp.data?.type || resp.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError('Failed to open document');
    } finally {
      setViewingDoc(null);
    }
  };

  const handleCreated = async (result) => {
    setAddOpen(false);
    setNotice({
      severity: result.passportUploadError ? 'warning' : 'success',
      text: result.passportUploadError || (result.reused ? 'This employee already existed and was linked.' : 'Employee added.')
    });
    setDocsByUid((prev) => { const next = { ...prev }; delete next[result.uid]; return next; });
    if (page !== 0) setPage(0); else await loadEmployees(0, rowsPerPage);
    setExpandedUid(result.uid);
    loadDocs(result.uid);
  };

  const handleUploaded = (uid, result) => {
    setUploadTarget(null);
    setNotice({ severity: 'success', text: `${result?.count || 1} document(s) uploaded.` });
    setExpandedUid(uid);
    loadDocs(uid);
  };

  // Search runs on the loaded page until the backend supports ?search= (task PE5).
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) => [employeeName(emp), emp.passportNumber, emp.emiratesId, emp.email]
      .some((v) => (v || '').toString().toLowerCase().includes(term)));
  }, [employees, search]);

  const colCount = canWrite ? 7 : 6;
  const stats = [
    { key: 'employees', label: 'Employees', value: total },
    ...(features.DOC_EXPIRY ? [
      { key: 'soon', label: 'Expiring in 30 days', value: expiry?.expiring0_30 ?? 0, color: 'warning.main', onClick: () => navigate('/portal/document-expiry') },
      { key: 'expired', label: 'Expired documents', value: expiry?.alreadyExpired ?? 0, color: 'error.main', onClick: () => navigate('/portal/document-expiry') },
    ] : []),
  ];

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}
          action={<Button size="small" onClick={() => loadEmployees(page, rowsPerPage)} sx={{ textTransform: 'none' }}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>
      )}

      <ProTableStats loading={loading && !employees.length} cards={stats} />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <TextField
          size="small"
          placeholder="Search by name, passport or Emirates ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {total} employees
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => loadEmployees(page, rowsPerPage)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canWrite && (
            <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={() => setAddOpen(true)}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', ml: 0.5 }}>
              Add Employee
            </Button>
          )}
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Name</TableCell>
                <TableCell>Passport</TableCell>
                <TableCell>Emirates ID</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                {canWrite && <TableCell align="center" sx={{ width: 72 }}>Upload</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {employees.length === 0 ? 'No employees yet' : 'No matching employees'}
                  </TableCell>
                </TableRow>
              ) : rows.map((emp) => {
                const uid = emp.uid;
                const name = employeeName(emp);
                const expanded = expandedUid === uid;
                const docState = docsByUid[uid];
                return (
                  <React.Fragment key={uid || emp.employeeId}>
                    <TableRow hover sx={{ cursor: 'pointer', '& > td': expanded ? { borderBottom: 0 } : undefined }} onClick={() => toggle(uid)}>
                      <TableCell>
                        <IconButton size="small" aria-label={expanded ? 'Hide documents' : 'Show documents'}>
                          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                      <TableCell>{emp.passportNumber || '—'}</TableCell>
                      <TableCell>{emp.emiratesId || '—'}</TableCell>
                      <TableCell>{emp.mobileNumber || emp.phone || '—'}</TableCell>
                      <TableCell>{emp.email || '—'}</TableCell>
                      {canWrite && (
                        <TableCell align="center" sx={{ py: 0 }}>
                          <Tooltip title="Upload documents">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setUploadTarget({ uid, name }); }}>
                              <UploadFileIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 0, border: 0 }} colSpan={colCount}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                            {docState?.loading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                            ) : docState?.error ? (
                              <Alert severity="error">{docState.error}</Alert>
                            ) : (docState?.groups || []).length === 0 ? (
                              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                No documents uploaded for this employee yet.
                              </Typography>
                            ) : (
                              <EmployeeDocuments
                                uid={uid}
                                groups={docState.groups}
                                onView={handleView}
                                viewingKey={viewingDoc}
                              />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {canWrite && (
        <>
          <AddClientEmployeeDialog open={addOpen} api={api} onClose={() => setAddOpen(false)} onCreated={handleCreated} />
          <UploadClientEmployeeDocumentsDialog
            open={!!uploadTarget}
            api={api}
            uid={uploadTarget?.uid}
            employeeName={uploadTarget?.name}
            documentTypes={documentTypes}
            onClose={() => setUploadTarget(null)}
            onUploaded={(result) => handleUploaded(uploadTarget?.uid, result)}
          />
        </>
      )}
    </Box>
  );
};

export default PortalEmployees;
